import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import auth from './en/auth.json';
import candidates from './en/candidates.json';
import common from './en/common.json';
import components from './en/components.json';
import dashboard from './en/dashboard.json';
import interviews from './en/interviews.json';
import navigation from './en/navigation.json';
import outstanding from './en/outstanding.json';
import questions from './en/questions.json';
import settings from './en/settings.json';
import vacancies from './en/vacancies.json';

/**
 * i18n is wired before the first user-visible string exists (`DESIGN.md` §11).
 *
 * Not because a second language is planned — none is — but because retrofitting i18n across
 * finished screens costs far more than the per-key tax of adding one now, and two components in
 * the design system already carry words of their own: a score's label and a status's name.
 *
 * English is the source language and the only catalogue. Keys are namespaced by area: `common` for
 * words the whole app shares, `components` for the design system, and one namespace per screen as
 * screens arrive.
 *
 * ⚠️ **`interviews` was registered *empty* by TASK-079**, which is the same decision this whole module
 * records one level up: i18n was wired before the first user-visible string existed, because
 * retrofitting it across finished screens costs far more than the per-key tax. Its keys arrive with the
 * screens that own their wording, and **TASK-080 wrote the first of them** — the blinded state's three
 * sentences, this screen's title, its way back, its own error and Forbidden lines, and two placeholders
 * §6.6/§6.7 delete when they are built. What is still deliberately absent is everything TASK-083–086
 * assign the *wording* of (the save indicator, the submit confirmation, the immutability line):
 * inventing copy for a screen `DESIGN.md` has not been read against is exactly the divergence
 * `.claude/rules/frontend.md` opens by warning about.
 */
export const resources = {
  en: {
    common,
    components,
    auth,
    navigation,
    dashboard,
    outstanding,
    candidates,
    vacancies,
    questions,
    interviews,
    settings,
  },
} as const;

export const defaultNamespace = 'common';

void i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  defaultNS: defaultNamespace,
  ns: [
    'common',
    'components',
    'auth',
    'navigation',
    'dashboard',
    'outstanding',
    'candidates',
    'vacancies',
    'questions',
    'interviews',
  ],
  interpolation: {
    // React escapes for us; double-escaping mangles a candidate's name.
    escapeValue: false,
  },
});

export default i18n;
